import React, { useRef, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ImageUp, X } from 'lucide-react';
import { toast } from 'sonner';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

const UploadPanel = ({ onAnalyze, loading }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState('');

  const pickFile = (f) => {
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error('Please upload a PNG, JPEG or WEBP screenshot');
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8MB)');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clear = () => {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        {!preview ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files?.[0]);
            }}
            className="w-full border-2 border-dashed border-gray-300 hover:border-purple-400 rounded-xl p-10 text-center transition-colors"
            data-testid="chart-upload-dropzone"
          >
            <ImageUp className="mx-auto text-purple-500 mb-3" size={32} />
            <p className="font-medium text-gray-800">Drop a chart screenshot here, or click to browse</p>
            <p className="text-xs text-gray-500 mt-1">PNG, JPEG or WEBP · max 8MB · include the price axis for exact levels</p>
          </button>
        ) : (
          <div className="relative">
            <img src={preview} alt="Chart preview" className="w-full rounded-xl border border-gray-200" data-testid="chart-upload-preview" />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5"
              data-testid="chart-upload-clear-btn"
              aria-label="Remove image"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0])}
          data-testid="chart-upload-input"
        />
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional context — e.g. 'TSLA 4h chart, thinking about a swing entry'"
          rows={2}
          maxLength={500}
          data-testid="chart-upload-notes"
        />
        <Button
          onClick={() => onAnalyze(file, notes)}
          disabled={!file || loading}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="chart-upload-analyze-btn"
        >
          {loading ? 'Analyzing chart…' : 'Analyze Screenshot'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default UploadPanel;
